import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { AdminAccessDenied } from './AdminAccessDenied';
import { supabase } from '../../lib/supabase';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Fonction pour vérifier l'accès au domaine actuel
function checkDomainAccess(currentDomain: string, allowedDomains: string[] | undefined, isSuperAdmin: boolean): boolean {
  // Super admin a toujours accès
  if (isSuperAdmin) {
    return true;
  }

  // Si pas de domaines autorisés définis ou liste vide, accès à tous les domaines
  if (!allowedDomains || allowedDomains.length === 0) {
    return true;
  }

  // Vérifier si le domaine actuel est dans la liste des domaines autorisés
  return allowedDomains.includes(currentDomain);
}

// Fonction pure pour vérifier l'accès à une route
function checkRouteAccess(path: string, restrictedRoutes: string[], isSuperAdmin: boolean): boolean {
  // Super admin a toujours accès
  if (isSuperAdmin) {
    return true;
  }

  // Si pas de routes restreintes, accès autorisé
  if (!restrictedRoutes || restrictedRoutes.length === 0) {
    return true;
  }

  // Normaliser le path (enlever trailing slash)
  const normalizedPath = path.endsWith('/') ? path.slice(0, -1) : path;

  // Vérifier chaque pattern de restriction
  for (const pattern of restrictedRoutes) {
    // Pattern avec wildcard (ex: /admin/portfolio/*)
    // Bloque la route de base ET toutes les sous-routes
    if (pattern.endsWith('/*')) {
      const baseRoute = pattern.slice(0, -2); // Enlever /*
      // Bloquer si c'est exactement la route de base OU une sous-route
      if (normalizedPath === baseRoute || normalizedPath.startsWith(baseRoute + '/')) {
        return false;
      }
    }
    // Pattern exact - bloque uniquement cette route exacte
    else if (normalizedPath === pattern) {
      return false;
    }
  }

  return true;
}

type AccessStatus = 'checking' | 'granted' | 'denied' | 'unauthorized';

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, checkAuth, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<AccessStatus>('checking');
  const lastCheckedPath = useRef<string>('');

  // Vérification combinée : auth + rôle + permissions
  useEffect(() => {
    let isMounted = true;
    
    const verifyAccess = async () => {
      const currentPath = location.pathname;
      
      // 1. Vérifier l'authentification Supabase
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // Non authentifié - rediriger vers login
        const redirectParam = encodeURIComponent(currentPath + location.search);
        if (currentPath.startsWith('/client')) {
          navigate(`/client/login?redirect=${redirectParam}`);
        } else {
          navigate(`/admin/login?redirect=${redirectParam}`);
        }
        return;
      }

      // 2. Récupérer les infos utilisateur depuis la DB
      const { data: userData, error } = await supabase
        .from('users')
        .select('role, is_active, permissions')
        .eq('id', session.user.id)
        .single();

      if (error || !userData) {
        console.error('[ProtectedRoute] Erreur récupération utilisateur:', error);
        if (isMounted) setStatus('unauthorized');
        return;
      }

      // 3. Vérifier si l'utilisateur est actif
      if (!userData.is_active) {
        await supabase.auth.signOut();
        navigate('/admin/login');
        return;
      }

      // 4. Pour les routes /admin/*, vérifier le rôle
      if (currentPath.startsWith('/admin')) {
        const allowedRoles = ['admin', 'super_admin'];
        if (!allowedRoles.includes(userData.role)) {
          console.log('[ProtectedRoute] Rôle non autorisé:', userData.role);
          if (isMounted) setStatus('denied');
          return;
        }

        // 5. Vérifier les restrictions de domaine
        const isSuperAdmin = userData.role === 'super_admin';
        const allowedDomains = userData.permissions?.admin_access?.allowed_domains || [];
        const currentDomain = window.location.hostname;
        
        console.log('[ProtectedRoute] Domain:', currentDomain);
        console.log('[ProtectedRoute] Allowed domains:', allowedDomains);
        
        const hasDomainAccess = checkDomainAccess(currentDomain, allowedDomains, isSuperAdmin);
        console.log('[ProtectedRoute] Domain access:', hasDomainAccess);
        
        if (!hasDomainAccess) {
          if (isMounted) setStatus('denied');
          return;
        }

        // 6. Vérifier les restrictions de route spécifiques
        const restrictedRoutes = userData.permissions?.admin_access?.restricted_routes || [];
        
        console.log('[ProtectedRoute] Path:', currentPath);
        console.log('[ProtectedRoute] Role:', userData.role);
        console.log('[ProtectedRoute] Restrictions:', restrictedRoutes);
        
        const hasAccess = checkRouteAccess(currentPath, restrictedRoutes, isSuperAdmin);
        console.log('[ProtectedRoute] Access granted:', hasAccess);
        
        if (!hasAccess) {
          if (isMounted) setStatus('denied');
          return;
        }
      }

      // Accès autorisé
      lastCheckedPath.current = currentPath;
      if (isMounted) setStatus('granted');
    };

    // Toujours revérifier quand le path change
    setStatus('checking');
    verifyAccess();

    return () => {
      isMounted = false;
    };
  }, [location.pathname, navigate]);

  // Affichage selon le statut
  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#001122] to-[#000811] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400">Vérification des permissions...</p>
        </div>
      </div>
    );
  }

  if (status === 'unauthorized') {
    return null;
  }

  if (status === 'denied') {
    return <AdminAccessDenied />;
  }

  return <>{children}</>;
}
